import React from 'react';
import NextImage from 'next/image';
import { Avatar, Spoiler } from '@mantine/core';
import { calcValue, currencyEnumBase, getTranslationText, isDefined, isEmpty, regionEnumBase, silenceError } from '@nfctron/nexus-core';
import {
	cn,
	countryEnumReact,
	eventCategoryEnumReact,
	Markdown,
	Slot,
	UiBadge,
	UiButton,
	UiDrawer,
	UiDrawerClose,
	UiDrawerContent,
	UiDrawerDescription,
	UiDrawerFooter,
	UiDrawerHeader,
	UiDrawerTitle,
	UiDrawerTrigger,
	UiFeatureIcon,
	UiLink,
	useFormat,
	useMediaQuery,
	useTranslationsCommon,
} from '@nfctron/nexus-react';
import {
	BitcoinIcon,
	BookOpenCheckIcon,
	ChevronDownIcon,
	ChevronRightIcon,
	ChevronUpIcon,
	CoinsIcon,
	CupSodaIcon,
	LandPlotIcon,
	LinkIcon,
	MapPinIcon,
	NfcIcon,
	TextIcon,
	TicketIcon,
	UserSearchIcon,
} from 'lucide-react';

import { useTranslationsScoped } from './EventCartDetail.i18n';
import { type EventCartDetailTypes as Types } from './EventCartDetail.types';

const SectionHeader = React.forwardRef<HTMLDivElement, Types.SectionHeaderProps>((props, ref) => {
	const { icon, className, label, ...rest } = props;
	return (
		<header className={cn('flex items-center gap-2 rounded-md bg-zinc-50 px-2 py-0.5 text-zinc-700', className)} ref={ref} {...rest}>
			<Slot className="size-3" aria-hidden="true">
				{icon}
			</Slot>
			<h2 className="text-sm font-semibold uppercase">{label}</h2>
		</header>
	);
});
SectionHeader.displayName = 'EventCartDetail/SectionHeader';

export const TakesPlaceItem = React.forwardRef<HTMLDivElement, Types.TakesPlaceItemProps>((props, ref) => {
	const { eventTakesPlaceFrom, eventTakesPlaceTo, eventOpenInfo, className, ...rest } = props;
	const _scoped = useTranslationsScoped();
	const format = useFormat();
	const eventStartDate = format.date(eventTakesPlaceFrom, 'PPP');
	const eventStartTime = format.date(eventTakesPlaceFrom, 'p');
	const eventMonth = format.date(eventTakesPlaceFrom, 'MMM');
	const eventDay = format.date(eventTakesPlaceFrom, 'dd');
	const eventDateRange = silenceError(() => format.eventDateRange(eventTakesPlaceFrom, eventTakesPlaceTo), '-');

	return (
		<section
			className={cn('flex items-center gap-2 justify-between px-3 py-2', className)}
			ref={ref}
			{...rest}
			itemScope
			itemType="https://schema.org/Event"
		>
			{/* content */}
			<div className="flex items-center gap-3 flex-grow max-w-full">
				{/* date calendar icon */}
				<div className="flex size-9 flex-shrink-0 flex-col overflow-hidden rounded-md bg-zinc-50" aria-hidden="true">
					<div className="bg-zinc-500 text-white text-center">
						{/* month header */}
						<time dateTime={eventTakesPlaceFrom.toISOString()} className="block font-bold text-2xs leading-[8px] p-0.5 uppercase">
							{eventMonth}
						</time>
					</div>
					{/* day */}
					<div className="flex flex-grow w-full flex-col rounded-b-md">
						<time dateTime={eventTakesPlaceFrom.toISOString()} className="font-semibold text-sm leading-[12px] text-center text-zinc-500 m-auto">
							{eventDay}
						</time>
					</div>
				</div>

				{/* date / time */}
				<div className="w-full">
					{/* date */}
					<time dateTime={eventTakesPlaceFrom.toISOString()} className="block text-base font-semibold">
						{eventDateRange}
					</time>
					{/* more date/time info */}
					<div className="block text-sm font-normal text-zinc-500">
						{eventOpenInfo ??
							_scoped.t('eventDateTime.label', {
								dateFrom: eventStartDate,
								startTime: eventStartTime,
							})}
					</div>
				</div>
			</div>
		</section>
	);
});
TakesPlaceItem.displayName = 'EventCartDetail/TakesPlaceItem';

export const PlaceVenueItem = React.forwardRef<HTMLDivElement, Types.PlaceVenueItemProps>((props, ref) => {
	const { eventPlace, eventRegionId, eventLocationVenue, className, ...rest } = props;
	const _common = useTranslationsCommon();
	const { region, country } = calcValue(() => {
		const reg = silenceError(() => regionEnumBase.get(eventRegionId), null);
		const ctr = isDefined(reg) ? silenceError(() => countryEnumReact.get(reg.countryId), null) : null;
		return { region: reg, country: ctr };
	});

	if (!isDefined(eventLocationVenue)) {
		return (
			<address
				className={cn('flex items-center gap-2 justify-between px-3 py-2 group not-italic', className)}
				ref={ref}
				{...rest}
				itemScope
				itemType="https://schema.org/Place"
			>
				<meta itemProp="name" content={eventPlace} />
				{isDefined(region) && isDefined(country) && (
					<React.Fragment>
						<meta itemProp="addressRegion" content={_common.t('region.label', null, { context: region.regionId })} />
						<meta itemProp="addressCountry" content={_common.t('country.label', null, { context: country.countryId })} />
					</React.Fragment>
				)}

				<div className="flex items-center gap-3 flex-grow max-w-full truncate text-zinc-500">
					<Avatar size={37} radius="md" aria-hidden="true">
						<MapPinIcon className="m-auto size-4 text-zinc-500" />
					</Avatar>
					{/*	text*/}
					<div>
						<span className="text-base font-semibold truncate text-zinc-900">{eventPlace}</span>
						{/* with region info */}
						{isDefined(region) && isDefined(country) && (
							<div className="flex items-center gap-1 leading-none">
								<country.Icon className="h-3.5 w-3.5" aria-hidden="true" />
								<span className="text-sm text-zinc-500">
									{[
										_common.t('region.label', null, { context: region.regionId }),
										_common.t('country.label', null, { context: country.countryId }),
									].join(', ')}
								</span>
							</div>
						)}
					</div>
				</div>
			</address>
		);
	}

	return (
		<UiButton asChild variant="ghost" size="unstyled" className="font-normal group">
			<UiLink
				variant="unstyled"
				href={`https://maps.google.com/maps?z=12&t=m&q=loc:${eventLocationVenue.latitude}+${eventLocationVenue.longitude}`}
				target="_blank"
				rel="noopener noreferrer"
			>
				<address
					className={cn('flex items-center gap-2 justify-between px-3 py-2 w-full not-italic', className)}
					ref={ref}
					{...rest}
					itemScope
					itemType="https://schema.org/Place"
				>
					<meta itemProp="name" content={eventLocationVenue.name} />
					<meta itemProp="latitude" content={eventLocationVenue.latitude.toString()} />
					<meta itemProp="longitude" content={eventLocationVenue.longitude.toString()} />
					{isDefined(eventLocationVenue.description) && <meta itemProp="description" content={eventLocationVenue.description} />}
					{isDefined(region) && isDefined(country) && (
						<React.Fragment>
							<meta itemProp="addressRegion" content={_common.t('region.label', null, { context: region.regionId })} />
							<meta itemProp="addressCountry" content={_common.t('country.label', null, { context: country.countryId })} />
						</React.Fragment>
					)}

					<div className="flex items-center gap-3 flex-grow truncate text-zinc-500">
						<Avatar size={37} radius="md" classNames={{ placeholder: 'bg-zinc-50' }} aria-hidden="true">
							<MapPinIcon className="m-auto size-4 text-zinc-500" />
						</Avatar>
						{/*	text*/}
						<div className="truncate">
							<span className="text-base font-semibold truncate text-zinc-900">{eventLocationVenue.name}</span>
							{/* with region */}
							{isDefined(region) && isDefined(country) && (
								<div className="truncate leading-none">
									<country.Icon className="mr-1 inline h-3.5 w-3.5" aria-hidden="true" />
									<span className="text-sm text-zinc-500">
										{[eventPlace, _common.t('country.label', null, { context: country.countryId })].join(', ')}
									</span>
								</div>
							)}
							{/* without region */}
							{!isDefined(region) && <span className="text-sm text-zinc-500">{eventPlace}</span>}
						</div>
					</div>

					{/* right icon */}
					<ChevronRightIcon
						className="size-4 text-zinc-500 opacity-0 group-hover:opacity-100 -left-1 group-hover:left-0 relative transition-all"
						aria-hidden="true"
					/>
				</address>
			</UiLink>
		</UiButton>
	);
});
PlaceVenueItem.displayName = 'EventCartDetail/PlaceVenueItem';

export const LinksSection = React.forwardRef<HTMLDivElement, Types.LinksSectionProps>((props, ref) => {
	const { eventLinks, className, ...rest } = props;
	const _scoped = useTranslationsScoped();
	return (
		<section className={cn('flex flex-col gap-2', className)} ref={ref} {...rest}>
			{/* section header */}
			<SectionHeader icon={<LinkIcon />} label={_scoped.t('linksSection.label', null)} />
			{/* content */}
			<nav className="flex flex-wrap gap-y-1" aria-label={_scoped.t('linksSection.label', null)}>
				{eventLinks.map((link, key) => (
					<UiButton variant="ghost" key={key} asChild className="group h-7 text-sm" style={{ paddingLeft: '8px', paddingRight: '8px' }}>
						<UiLink variant="unstyled" href={link.href} target="_blank">
							<div className="flex items-center gap-1 mr-auto">
								<Avatar
									size={19}
									radius="md"
									src={silenceError(() => `https://www.google.com/s2/favicons?domain=${link.href}&sz=180`)}
									alt={link.label}
									aria-hidden="true"
								>
									<LinkIcon className="m-auto text-sm" />
								</Avatar>
								{/* text */}
								<span className="truncate">{link.label}</span>
							</div>
							{/* right icon */}
							<ChevronRightIcon className="size-3 -left-0.5 group-hover:left-0 relative transition-all" aria-hidden="true" />
						</UiLink>
					</UiButton>
				))}
			</nav>
		</section>
	);
});
LinksSection.displayName = 'EventCartDetail/LinksSection';

export const DescriptionSection = React.forwardRef<HTMLDivElement, Types.DescriptionSectionProps>((props, ref) => {
	const { eventDescription, eventDescriptionMd, className, ...rest } = props;
	const _scoped = useTranslationsScoped();
	const isDesktop = useMediaQuery('(min-width: 768px)', { initialValue: true });
	const contentRef = React.useRef<HTMLDivElement>(null);
	const refClientHeight = contentRef.current?.clientHeight ?? 185;
	const maxHeight = isDesktop ? Math.min(refClientHeight, 185) + 10 : 9999;
	const isCollapsed = refClientHeight > maxHeight;

	return (
		<Spoiler
			maxHeight={maxHeight}
			hideLabel={
				<UiButton
					asChild
					variant="subtle"
					rightSection={<ChevronUpIcon className="size-3" aria-hidden="true" />}
					size="unstyled"
					className="w-full px-2 py-1 gap-2 text-sm"
					aria-label={_scoped.t('hideDescriptionButton.label', null)}
				>
					<span>{_scoped.t('hideDescriptionButton.label', null)}</span>
				</UiButton>
			}
			showLabel={
				<UiButton
					asChild
					variant="subtle"
					rightSection={<ChevronDownIcon className="size-3" aria-hidden="true" />}
					size="unstyled"
					className="w-full px-2 py-1 gap-2 text-sm"
					aria-label={_scoped.t('showDescriptionButton.label', null)}
				>
					<span>{_scoped.t('showDescriptionButton.label', null)}</span>
				</UiButton>
			}
			classNames={{
				control: 'w-full',
				root: isCollapsed ? 'pb-4' : 'pb-0',
			}}
		>
			<section className={cn('flex flex-col gap-2', className)} {...rest} ref={contentRef} itemProp="description">
				{/* header */}
				<SectionHeader icon={<TextIcon />} label={_scoped.t('descriptionSection.label', null)} />
				{/* description */}
				<div className="px-3">
					{!isEmpty(eventDescriptionMd) ? (
						<Markdown className="text-zinc-700 text-base" componentProps={{ p: { className: 'text-base not-last:mb-2' } }}>
							{eventDescriptionMd}
						</Markdown>
					) : (
						<p className="text-sm opacity-75">{eventDescription || _scoped.t('noDescription.text', null)}</p>
					)}
				</div>
			</section>
		</Spoiler>
	);
});
DescriptionSection.displayName = 'EventCartDetail/DescriptionSection';

const OrganizerSection = React.forwardRef<HTMLDivElement, Types.OrganizerSectionProps>((props, ref) => {
	const { organizerName, organizerLogoUrl, organizerLinkUrl, className, ...rest } = props;
	const _scoped = useTranslationsScoped();
	return (
		<section className="flex flex-col gap-2" itemScope itemType="https://schema.org/Organization">
			{/* hidden metadata for SEO */}
			<meta itemProp="name" content={organizerName} />
			{isDefined(organizerLogoUrl) && <meta itemProp="logo" content={organizerLogoUrl} />}

			{/* header */}
			<SectionHeader icon={<UserSearchIcon />} label={_scoped.t('organizerSection.label', null)} />

			{/* organizer events link */}
			<UiButton asChild variant="ghost" size="unstyled" className="font-normal group">
				<UiLink variant="unstyled" target="_blank" href={organizerLinkUrl} rel="noopener">
					<div className={cn('flex items-center gap-2 justify-between px-2.5 py-1.5 flex-grow', className)} ref={ref} {...rest}>
						<div className="flex items-start gap-3">
							{/* avatar */}
							<div className="mt-1 rounded-md border border-gray-200 bg-gray-50">
								<Avatar
									size={37}
									radius="md"
									src={organizerLogoUrl}
									alt={organizerName}
									styles={{ image: { padding: '3px', objectFit: 'scale-down' } }}
								>
									<UserSearchIcon className="m-auto size-4 text-gray-500" aria-hidden="true" />
								</Avatar>
							</div>

							{/*	text*/}
							<div>
								<span className="text-base block font-semibold truncate">{organizerName}</span>
								<span className="text-sm block text-zinc-500 whitespace-break-spaces">{_scoped.t('organizerMoreEvents.text', null)}</span>
							</div>
						</div>

						{/* right icon */}
						<ChevronRightIcon
							className="w-4 h-4 text-zinc-500 shrink-0 opacity-0 group-hover:opacity-100 -left-1 group-hover:left-0 relative transition-all"
							aria-hidden="true"
						/>
					</div>
				</UiLink>
			</UiButton>
		</section>
	);
});
OrganizerSection.displayName = 'EventCartDetail/OrganizerSection';

const EventGroupsSection = React.forwardRef<HTMLDivElement, Types.EventGroupsSectionProps>((props, ref) => {
	const { eventGroups, className, ...rest } = props;
	const _scoped = useTranslationsScoped();

	return (
		<section className="flex flex-col gap-2">
			{/* header */}
			<SectionHeader icon={<TicketIcon />} label={_scoped.t('eventGroupsSection.label', null)} />

			{/* event group links */}
			<nav className="flex flex-col gap-1" aria-label={_scoped.t('eventGroupsSection.label', null)}>
				{eventGroups.map((eventGroup) => (
					<UiButton key={eventGroup.eventGroupId} asChild variant="ghost" size="unstyled" className="font-normal group">
						<UiLink variant="unstyled" target="_blank" href={eventGroup.linkUrl} rel="noopener">
							<div className={cn('flex items-center gap-2 justify-between px-2.5 py-1.5 flex-grow', className)} ref={ref} {...rest}>
								<div className="flex items-center gap-3">
									{/* avatar */}
									<div className="mt-1 rounded-md border border-gray-200 bg-gray-50">
										<Avatar size={37} radius="md" src={eventGroup.logoUrl} alt="" styles={{ image: { padding: '3px', objectFit: 'scale-down' } }}>
											<TicketIcon className="m-auto size-4 text-gray-500" aria-hidden="true" />
										</Avatar>
									</div>

									{/*	text*/}
									<div>
										<span className="text-base block font-semibold truncate">{eventGroup.namePub}</span>
										{isDefined(eventGroup.subtitle) && (
											<span className="text-sm block text-zinc-500 whitespace-break-spaces">{eventGroup.subtitle}</span>
										)}
									</div>
								</div>

								{/* right icon */}
								<ChevronRightIcon
									className="w-4 h-4 text-zinc-500 shrink-0 opacity-0 group-hover:opacity-100 -left-1 group-hover:left-0 relative transition-all"
									aria-hidden="true"
								/>
							</div>
						</UiLink>
					</UiButton>
				))}
			</nav>
		</section>
	);
});
EventGroupsSection.displayName = 'EventCartDetail/EventGroupsSection';

const EventSpecificItemButton = React.forwardRef<HTMLButtonElement, Types.EventSpecificItemButtonProps>((props, ref) => {
	const { label, description, icon, className, ...rest } = props;
	const _scoped = useTranslationsScoped();
	return (
		<UiDrawer>
			<UiDrawerTrigger asChild>
				<UiButton
					ref={ref}
					className={cn('group !text-sm px-2', className)}
					size="xs"
					variant="ghost"
					leftSection={icon}
					rightSection={<ChevronRightIcon className="size-3 -left-0.5 group-hover:left-0 relative transition-all" aria-hidden="true" />}
					{...rest}
				>
					{label}
				</UiButton>
			</UiDrawerTrigger>
			<UiDrawerContent className="mx-auto max-w-screen-sm">
				<UiDrawerHeader className="flex items-start justify-between gap-3 text-left p-4">
					<div className="flex flex-col gap-2.5">
						<UiFeatureIcon
							className="mb-3 size-10"
							icon={
								<span className="size-6" aria-hidden="true">
									{icon}
								</span>
							}
						/>
						<UiDrawerTitle className="text-xl">{label}</UiDrawerTitle>
						<UiDrawerDescription>
							<Markdown className="text-sm text-zinc-700" componentProps={{ p: { className: 'mb-2' } }}>
								{description}
							</Markdown>
						</UiDrawerDescription>
					</div>
				</UiDrawerHeader>

				<UiDrawerFooter>
					<UiDrawerClose asChild>
						<UiButton className="text-base" variant="secondary">
							{_scoped.t('closeEventSpecificButton.label', null)}
						</UiButton>
					</UiDrawerClose>
				</UiDrawerFooter>
			</UiDrawerContent>
		</UiDrawer>
	);
});
EventSpecificItemButton.displayName = 'EventCartDetail/EventSpecificItemButton';

export const SpecificsSection = React.forwardRef<HTMLDivElement, Types.SpecificsSectionProps>((props, ref) => {
	const {
		eventCurrencyId,
		eventExtraCurrencies,
		eventVirtualCurrency,
		onSitePayment,
		withReturnableCupsControl,
		withZoneControl,
		className,
		...rest
	} = props;
	const _scoped = useTranslationsScoped();
	const format = useFormat();

	/** return null if no specifics to show */
	if (
		!isDefined(onSitePayment) &&
		!isDefined(withReturnableCupsControl) &&
		!isDefined(withZoneControl) &&
		!isDefined(eventVirtualCurrency) &&
		eventExtraCurrencies.length === 0
	)
		return null;

	return (
		<section className={cn('flex flex-col gap-2', className)} ref={ref} {...rest}>
			{/*	section header */}
			<SectionHeader icon={<BookOpenCheckIcon />} label={_scoped.t('specificsSection.label', null)} />
			{/* content */}
			<div className="flex flex-wrap gap-y-1" role="list">
				{/* on-site payment */}
				{isDefined(onSitePayment) && (
					<EventSpecificItemButton
						icon={<NfcIcon />}
						label={
							getTranslationText(onSitePayment.label, _scoped.language.code) ??
							_scoped.t('onSitePaymentDefault.label', null, { context: onSitePayment.type })
						}
						description={
							getTranslationText(onSitePayment.description, _scoped.language.code) ??
							_scoped.t('onSitePaymentDefault.description', null, { context: onSitePayment.type })
						}
					/>
				)}
				{/* virtual currency */}
				{isDefined(eventVirtualCurrency) && (
					<EventSpecificItemButton
						icon={<BitcoinIcon />}
						label={_scoped.t('virtualCurrencyDefault.label', { name: eventVirtualCurrency.name })}
						description={_scoped.t('virtualCurrencyDefault.description', {
							name: eventVirtualCurrency.name,
							...(eventVirtualCurrency.rate > 1
								? {
										/** e.g. 1 bolt = 100 Kč */
										lower: format.virtualPrice(100 * eventVirtualCurrency.rate, eventVirtualCurrency),
										higher: format.price(eventVirtualCurrency.rate * 100, eventCurrencyId),
								  }
								: {
										/** e.g. 1 Kč = 100 bolts */
										lower: format.price(100, eventCurrencyId),
										higher: format.virtualPrice(100, eventVirtualCurrency),
								  }),
						})}
					/>
				)}
				{/* extra currencies */}
				{isDefined(eventExtraCurrencies) &&
					eventExtraCurrencies.length > 0 &&
					eventExtraCurrencies.map((extraCurrency) => {
						const currency = currencyEnumBase.get(extraCurrency.currencyId);
						return (
							<EventSpecificItemButton
								key={extraCurrency.currencyId}
								icon={<CoinsIcon />}
								label={_scoped.t('extraCurrencyDefault.label', { name: currency.isoChar })}
								description={_scoped.t('extraCurrencyDefault.description', {
									name: currency.isoChar,
									...(extraCurrency.rate > 1
										? {
												/** e.g. 1 EUR = 24 Kč */
												lower: format.price(100, currency.currencyId),
												higher: format.price(extraCurrency.rate * 100, eventCurrencyId),
										  }
										: {
												/** e.g. 1 Kč = 6 yen */
												lower: format.price(100, eventCurrencyId),
												higher: format.price((1 / extraCurrency.rate) * 100, currency.currencyId),
										  }),
								})}
							/>
						);
					})}
				{/* returnable cups control */}
				{isDefined(withReturnableCupsControl) && (
					<EventSpecificItemButton
						icon={<CupSodaIcon />}
						label={
							getTranslationText(withReturnableCupsControl.label, _scoped.language.languageId) ??
							_scoped.t('returnableCupsControlDefault.label', null)
						}
						description={
							getTranslationText(withReturnableCupsControl.description, _scoped.language.languageId) ??
							_scoped.t('returnableCupsControlDefault.description', { maxCups: withReturnableCupsControl.maximumCups ?? 3 })
						}
					/>
				)}
				{/* zone control */}
				{isDefined(withZoneControl) && (
					<EventSpecificItemButton
						icon={<LandPlotIcon />}
						label={getTranslationText(withZoneControl.label, _scoped.language.languageId) ?? _scoped.t('zoneControlDefault.label', null)}
						description={
							getTranslationText(withZoneControl.description, _scoped.language.languageId) ?? _scoped.t('zoneControlDefault.description', null)
						}
					/>
				)}
			</div>
		</section>
	);
});
SpecificsSection.displayName = 'EventCartDetail/SpecificsSection';

/**
 * EventCartDetail component
 * Displays detailed information about an event including location, dates, description,
 * and other relevant details
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
export const EventCartDetail = React.forwardRef<HTMLDivElement, Types.Props>((props, ref) => {
	const {
		eventGroups,
		eventCategories,
		eventColors,
		eventSubtitle,
		eventTakesPlaceFrom,
		eventTakesPlaceTo,
		eventOpenInfo,
		eventLogoUrl,
		headerImageUrl,
		eventName,
		eventPlace,
		eventLocationVenue,
		eventRegionId,
		eventDescription,
		eventDescriptionMd,
		organizerLogoUrl,
		organizerLinkUrl,
		organizerName,
		hideOrganizerInCart,
		eventLinks,
		eventVirtualCurrency,
		eventCurrencyId,
		eventExtraCurrencies,
		onSitePayment,
		withReturnableCupsControl,
		withZoneControl,
		className,
		...rest
	} = props;
	const _scoped = useTranslationsScoped();
	const _common = useTranslationsCommon();
	const isDesktop = useMediaQuery('(min-width: 768px)', { initialValue: true });
	const [isSpoilerOpen, setIsSpoilerOpen] = React.useState(false);

	return (
		<article
			ref={ref}
			className={cn('group/header relative flex w-full flex-col rounded-xl bg-white p-2', className)}
			{...rest}
			itemScope
			itemType="https://schema.org/Event"
		>
			{/* upper image frame */}
			{!isEmpty(headerImageUrl) && (
				<figure className="relative rounded-lg bg-zinc-200 border border-zinc-200 overflow-hidden">
					<NextImage
						className="relative z-10 mx-auto max-h-[350px] object-contain rounded-lg"
						priority
						src={headerImageUrl}
						alt={eventName}
						height={240}
						width={480}
						sizes="100vw"
						style={{
							width: '100%',
							height: 'auto',
						}}
					/>
				</figure>
			)}

			{/* header info */}
			<header className="flex flex-col mb-2">
				{/* event categories */}
				{eventCategories.length > 0 && (
					<div className="flex flex-wrap items-center gap-1 px-2 pt-2 pb-0" role="list">
						{eventCategories.map((categoryId) => {
							const eventCategory = eventCategoryEnumReact.get(categoryId);
							return (
								<UiBadge
									key={categoryId}
									size="unstyled"
									className="h-5 px-1.5 py-0.5 text-xs gap-1"
									rounded="full"
									leftSection={<eventCategory.Icon className="size-2.5" aria-hidden="true" />}
									style={{
										backgroundColor: eventColors.background,
										color: eventColors.subtitle,
									}}
									role="listitem"
								>
									{_common.t('eventCategory.label', null, { context: eventCategory.eventCategoryId })}
								</UiBadge>
							);
						})}
					</div>
				)}

				{/* event + organizer info */}
				<div className="flex flex-row items-start justify-between gap-3 p-2">
					{/* texts */}
					<div className="leading-none">
						{/* event name */}
						<h1 className="font-bold text-2xl md:text-3xl leading-snug text-zinc-900" itemProp="name">
							{eventName}
						</h1>
						{/* event subtitle */}
						{!isEmpty(eventSubtitle) && (
							<span
								style={{ lineHeight: '1.3' }}
								className="text-sm font-semibold block whitespace-pre-wrap text-zinc-500"
								title={eventSubtitle}
								itemProp="alternateName"
							>
								{eventSubtitle}
							</span>
						)}
					</div>

					{/* event logo */}
					{!isEmpty(eventLogoUrl) && (
						<NextImage
							className="max-h-[50px] max-w-[125px] flex-shrink-0 object-scale-down object-right"
							src={eventLogoUrl}
							alt={`Logo ${eventName}`}
							width={125}
							height={50}
							style={{ objectFit: 'scale-down', objectPosition: 'right' }}
							itemProp="image"
						/>
					)}
				</div>

				{/* lower content */}
				<div className="flex flex-col gap-1">
					{/* date / time */}
					<TakesPlaceItem eventTakesPlaceFrom={eventTakesPlaceFrom} eventTakesPlaceTo={eventTakesPlaceTo} eventOpenInfo={eventOpenInfo} />

					{/* venue / place */}
					<PlaceVenueItem eventPlace={eventPlace} eventRegionId={eventRegionId} eventLocationVenue={eventLocationVenue} />
				</div>
			</header>

			{/* links */}
			{eventLinks.length > 0 && <LinksSection className="mb-2" eventLinks={eventLinks} />}

			{/* other content */}
			<Spoiler
				maxHeight={isDesktop ? 9999 : 0}
				expanded={isSpoilerOpen}
				onExpandedChange={setIsSpoilerOpen}
				hideLabel={
					<UiButton
						asChild
						variant="subtle"
						color="lucky-point"
						rightSection={<ChevronUpIcon className="size-3" aria-hidden="true" />}
						size="unstyled"
						className="w-full px-2 py-1 gap-2 text-sm"
						aria-expanded="true"
						aria-label={_scoped.t('hideOtherContentButton.label', null)}
					>
						<span>{_scoped.t('hideOtherContentButton.label', null)}</span>
					</UiButton>
				}
				showLabel={
					<UiButton
						asChild
						variant="subtle"
						color="lucky-point"
						rightSection={<ChevronDownIcon className="size-3" aria-hidden="true" />}
						size="unstyled"
						className="w-full px-2 py-1 gap-2 text-sm"
						aria-expanded="false"
						aria-label={_scoped.t('showOtherContentButton.label', null)}
					>
						<span>{_scoped.t('showOtherContentButton.label', null)}</span>
					</UiButton>
				}
				classNames={{
					control: 'w-full static order-[-1]',
					root: cn(
						'flex flex-col gap-2 mb-0!',
						/** initial mobile styles to prevent layout shifts */
						'max-h-0 pb-6 md:max-h-none md:pb-0 transition-[max-height] duration-500 ease-in-out',
						isSpoilerOpen && 'max-h-[5000px] pb-0',
					),
				}}
			>
				<div className="flex flex-col gap-3 pt-2">
					{/* description section */}
					<DescriptionSection eventDescription={eventDescription} eventDescriptionMd={eventDescriptionMd} />

					{/* specifics section */}
					<SpecificsSection
						eventCurrencyId={eventCurrencyId}
						eventExtraCurrencies={eventExtraCurrencies}
						eventVirtualCurrency={eventVirtualCurrency}
						onSitePayment={onSitePayment}
						withReturnableCupsControl={withReturnableCupsControl}
						withZoneControl={withZoneControl}
					/>

					{/* event groups section */}
					{eventGroups.length > 0 && <EventGroupsSection eventGroups={eventGroups} />}
					{/* organizer section */}
					{!hideOrganizerInCart && (
						<OrganizerSection organizerName={organizerName} organizerLogoUrl={organizerLogoUrl} organizerLinkUrl={organizerLinkUrl} />
					)}
				</div>
			</Spoiler>
		</article>
	);
});
EventCartDetail.displayName = 'EventCartDetail';
